"use client";

import { useEffect } from "react";

import { clientLogger } from "@/lib/logging/client-logger";

/**
 * Root React error boundary (Next.js app-router convention). Catches render
 * errors that escape the route tree — including ones an in-tree error boundary
 * would otherwise swallow before they reach `window.onerror`. Reports to Loki
 * via the client logger, then shows a minimal recover-able fallback.
 *
 * `global-error` replaces the root layout when it fires, so it must render its
 * own <html>/<body>.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    clientLogger.error("React render error (global-error boundary)", {
      kind: "react-error-boundary",
      name: error.name,
      message: error.message,
      stack: error.stack,
      digest: error.digest,
    });
    clientLogger.flush();
  }, [error]);

  return (
    <html lang="en">
      <body style={{ fontFamily: "system-ui, sans-serif", padding: "2rem" }}>
        <h2>Something went wrong</h2>
        <p>The error has been reported. You can try again.</p>
        <button type="button" onClick={() => reset()} style={{ marginTop: "1rem", padding: "0.5rem 1rem" }}>
          Try again
        </button>
      </body>
    </html>
  );
}
